import { useEffect, useState } from 'react';
import { learningApi } from '../../../api/learning.js';
import { hrApi } from '../../../api/hr.js';

/**
 * My Learning — enroll an employee in a course, track progress, and take the
 * assessment to complete (which issues a certification + grants any linked
 * skill). As an admin the employee is selectable; employees self-serve their own.
 */
export default function MyLearning() {
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [enrollments, setEnrollments] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      hrApi.listEmployees().catch(() => ({ items: [] })),
      learningApi.listCourses({ active: true }),
    ])
      .then(([e, c]) => {
        setEmployees(e?.items || e || []);
        setCourses(c?.items || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  async function loadEnrollments(empId) {
    if (!empId) {
      setEnrollments([]);
      return;
    }
    setLoading(true);
    try {
      const res = await learningApi.listEnrollments({ employee_id: empId });
      setEnrollments(res?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }

  function onSelectEmployee(id) {
    setEmployeeId(id);
    setActive(null);
    setNotice(null);
    loadEnrollments(id);
  }

  async function enroll(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const courseId = fd.get('courseId');
    if (!employeeId || !courseId) {
      setError('Select an employee and a course first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { enrollment } = await learningApi.enroll({ employeeId, courseId });
      await loadEnrollments(employeeId);
      setActive(enrollment);
      setNotice(`Enrolled in "${enrollment.course_title}"`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enroll');
    } finally {
      setBusy(false);
    }
  }

  async function saveProgress(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const { enrollment } = await learningApi.updateProgress(active.id, { progressPct: Number(fd.get('progressPct')) });
      setActive(enrollment);
      await loadEnrollments(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update progress');
    } finally {
      setBusy(false);
    }
  }

  async function complete(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const res = await learningApi.complete(active.id, { score: Number(fd.get('score')) });
      setActive(res.enrollment);
      await loadEnrollments(employeeId);
      setNotice(
        res.passed
          ? `Completed! ${res.certification ? `Certificate "${res.certification.name}" issued.` : ''}${res.skillGranted ? ' Skill granted.' : ''}`
          : 'Score below the passing mark — marked as failed (no certificate).'
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="learning-mylearning-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>My Learning</h1>
          <p className="muted">Enroll, track progress, and take assessments</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="learning-mylearning-error">{error}</div>}
      {notice && <div className="alert info" data-testid="learning-mylearning-notice">{notice}</div>}

      <form onSubmit={enroll} className="card form-card grid-form">
        <label>Employee
          <select data-testid="learning-enroll-employee-input" value={employeeId} onChange={(e) => onSelectEmployee(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <label>Course
          <select name="courseId" data-testid="learning-enroll-course-input" defaultValue="">
            <option value="" disabled>— select course —</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="learning-enroll" disabled={busy}>Enroll</button></div>
      </form>

      <div className="card">
        <h3>Enrollments</h3>
        <table className="data-table" data-testid="learning-enrollment-list">
          <thead><tr><th>Course</th><th>Status</th><th>Progress</th><th></th></tr></thead>
          <tbody>
            {enrollments.map((en) => (
              <tr key={en.id} data-testid={`learning-enrollment-row-${en.id}`} className={active?.id === en.id ? 'active' : ''}>
                <td>{en.course_title}</td>
                <td data-testid={`learning-enrollment-status-${en.id}`}>{en.status}</td>
                <td>{Number(en.progress_pct)}%</td>
                <td><button className="btn ghost" data-testid={`learning-enrollment-open-${en.id}`} onClick={() => { setActive(en); setNotice(null); }}>Open</button></td>
              </tr>
            ))}
            {!loading && employeeId && enrollments.length === 0 && <tr><td colSpan={4} className="muted">No enrollments yet.</td></tr>}
            {!employeeId && <tr><td colSpan={4} className="muted">Select an employee to see enrollments.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="card" data-testid="learning-active-enrollment">
          <h3>{active.course_title} — <span data-testid="learning-active-status">{active.status}</span></h3>
          <form onSubmit={saveProgress} className="inline-form">
            <label>Progress (%)
              <input name="progressPct" type="number" min="0" max="100" defaultValue={Number(active.progress_pct)} data-testid="learning-progress-input" />
            </label>
            <button className="btn" type="submit" data-testid="learning-progress" disabled={busy}>Save progress</button>
          </form>
          <form onSubmit={complete} className="inline-form">
            <label>Assessment score (%)
              <input name="score" type="number" min="0" max="100" defaultValue={100} data-testid="learning-complete-score-input" />
            </label>
            <button className="btn primary" type="submit" data-testid="learning-complete" disabled={busy}>Complete course</button>
          </form>
        </div>
      )}
    </section>
  );
}
